import React from "react";

import { EnCamino } from "./tipos";

/*
	Piezas visuales de «En Camino» (docs/ESPEC_PROMO_EN_CAMINO.md §4): el aviso dorado de Registrar Pago, la franja del Estado de Cuenta
	y la barra de avance de la lista de Promociones. Los textos salen de aquí para que digan lo mismo en todas partes.
*/

const DIAS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/** «L8,000» / «$1,200.50»: el símbolo del plantel pegado, sin decimales cuando son cero. */
export const montoEC = (sym: string, n: number | null | undefined): string =>
	(sym || "") + (Math.round((Number(n) || 0) * 100) / 100).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** «lun 16-nov» (la fecha viene `YYYY-MM-DD`, sin zona: no se pasa por Date local). */
export const fechaEC = (iso: string | null | undefined): string => {
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
	if (!m) return "";
	const dia = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
	return DIAS[dia] + " " + Number(m[3]) + "-" + MESES[Number(m[2]) - 1];
};

export const avanceEC = (e: Pick<EnCamino, "paid" | "target">): number => {
	const t = Number(e.target) || 0;
	return t > 0.004 ? Math.max(0, Math.min(100, Math.round(((Number(e.paid) || 0) / t) * 100))) : 0;
};

export const esPorCobrar = (e: Pick<EnCamino, "status">) => e.status === "porCobrar";

export const S_EC = {
	title: "En Camino",
	porCobrar: "Por Cobrar",
	minToday: (sym: string, n: number) => `mínimo hoy ${montoEC(sym, n)}`,
	leva: (sym: string, paid: number, target: number) => `lleva ${montoEC(sym, paid)} de ${montoEC(sym, target)}`,
	faltan: (sym: string, n: number) => `faltan ${montoEC(sym, n)}`,
	vence: (iso: string) => `vence ${fechaEC(iso)}`,
	dias: (n: number) => (n <= 0 ? "Vence Hoy" : n === 1 ? "1 Día" : `${n} Días`),
	history: "Historial",
};

/** Barra delgada de avance: relleno dorado sobre pista suave. */
export const BarraAvance: React.FC<{ e: Pick<EnCamino, "paid" | "target">; alto?: number }> = ({ e, alto = 4 }) => (
	<div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={avanceEC(e)}
		style={{ height: alto, borderRadius: alto, background: "var(--wa-filo)", overflow: "hidden", marginTop: 6 }}>
		<div style={{ width: avanceEC(e) + "%", height: "100%", background: "var(--wa-oro)", transition: "width .2s" }} />
	</div>
);

/** La frase del aviso: «8x4 En Camino: lleva L8,000 de L14,600, faltan L6,600, vence lun 16-nov». */
export const FraseEnCamino: React.FC<{ e: EnCamino; sym: string; conNombre?: boolean }> = ({ e, sym, conNombre = true }) => {
	const s = e.currencySymbol || sym;
	if (esPorCobrar(e)) {
		return <><b>{conNombre ? e.promoName + " " : ""}{S_EC.porCobrar}:</b> {S_EC.minToday(s, e.minToday)}</>;
	}
	const partes = [S_EC.leva(s, e.paid, e.target), S_EC.faltan(s, e.remaining)];
	if (e.dueDate) partes.push(S_EC.vence(e.dueDate));
	return <><b>{conNombre ? e.promoName + " " : ""}{S_EC.title}:</b> {partes.join(", ")}</>;
};

/** Aviso dorado de una promo En Camino o Por Cobrar, con su barra de avance. */
const BandaEnCamino: React.FC<{ e: EnCamino; sym: string; conNombre?: boolean; style?: React.CSSProperties }> = ({ e, sym, conNombre = true, style }) => (
	<div style={{ border: "1px solid var(--wa-oro)", background: "var(--wa-oro-suave)", padding: "8px 10px", marginTop: 8, fontSize: 13, ...style }}>
		<div><FraseEnCamino e={e} sym={sym} conNombre={conNombre} /></div>
		{esPorCobrar(e) ? null : <BarraAvance e={e} />}
	</div>
);

export default BandaEnCamino;
